// src/utils/releveEcarts.ts — SCE Issues
// Le relevé d'écarts P&ID / PPL (refonte de l'ouverture, étape 3a — 14/10,
// docs/chantier-fnct.md § 13). Pur, testable.
//
// Un relevé est une FNC-T à un stade précoce : le N° est pris au premier écart,
// la colonne Ouverture reste vide jusqu'à l'ouverture. Les écarts vivent dans la
// colonne `Ecarts` de SCE_FNCT, texte multiligne BRUT, UNE LIGNE JSON PAR ÉCART :
//
//   {"id":"…","support":"pid","nature":"manquant","repere":"Pressure transmitter H-PT-3010",
//    "auteur":"Hugo Peultier","auteurUPN":"hugo.peultier@sartorius.com",
//    "date":"2026-10-14T08:12:00.000Z","probleme":"P2026-0207","fichiers":[]}
//
// Règles :
//  - une ligne illisible est GARDÉE telle quelle, jamais perdue ni réécrite ;
//  - un ajout se fait en fin de texte, sans toucher aux lignes déjà là ;
//  - la phrase anglaise (« The reference of NVS11900 is wrong on the PPL. ») se
//    CALCULE avec les règles de l'escalade (phraseEcart), elle n'est pas stockée ;
//  - `fichiers` : des NOMS de fichiers de SCE_PiecesJointes, jamais d'adresse
//    complète (les listes doivent pouvoir déménager sur QM_SCE).

import type { Fnct, Jour } from '@/types/fnct.types'
import { ANNULE, CATEGORIES_PAR_SUFFIXE, cleRapprochement, estActif, suffixeNumeroSerie, type Equipement, type Issue } from '@/types/issue.types'
import { nvsDeFourniture } from '@/config/equipements.catalogue'
import { CATEGORIE_PROCESS } from '@/config/fnct.config'
import { comparerNumeros, estEnReleve, formatNumero } from './fnct'
import {
  NATURES_ECART,
  SUPPORTS_ECART,
  phraseEcart,
  typeProduitDe,
  type NatureEcart,
  type SupportEcart,
} from './escaladeFnct'

export interface EcartReleve {
  /** Identifiant unique de l'écart : sert à vérifier, après écriture, qu'il est bien là. */
  id: string
  support: SupportEcart
  nature: NatureEcart
  /** Le repère (« NVS11900 », « Manual valve H-HV-2041 ») ou, pour « Autre », la phrase, en anglais. */
  repere: string
  /** « Prénom Nom » de qui l'a relevé. */
  auteur: string
  auteurUPN: string
  /** Date et heure du relevé, ISO 8601. */
  date: string
  /** ID du problème lié (`P2026-0207`) ; vide si l'écart est relevé sans problème. */
  probleme: string
  /** Noms de fichiers dans SCE_PiecesJointes (dossier du problème, ou du N° de FNC-T). */
  fichiers: string[]
}

/** Une ligne de la colonne : un écart lu, ou une ligne illisible gardée telle quelle. */
export type LigneEcart = { ecart: EcartReleve; brute: string } | { ecart: null; brute: string }

const SUPPORTS = new Set<string>(SUPPORTS_ECART.map((s) => s.valeur))
const NATURES = new Set<string>(NATURES_ECART.map((n) => n.valeur))

const texte = (v: unknown): string => (typeof v === 'string' ? v : '')

/** Une ligne JSON → l'écart, ou null si elle ne se lit pas (JSON cassé, document ou nature inconnus). */
export function lireLigneEcart(ligne: string): EcartReleve | null {
  let o: unknown
  try {
    o = JSON.parse(ligne)
  } catch {
    return null
  }
  if (typeof o !== 'object' || o === null || Array.isArray(o)) return null
  const r = o as Record<string, unknown>
  if (typeof r.support !== 'string' || !SUPPORTS.has(r.support)) return null
  if (typeof r.nature !== 'string' || !NATURES.has(r.nature)) return null
  if (typeof r.repere !== 'string' || !r.repere.trim()) return null
  return {
    id: texte(r.id),
    support: r.support as SupportEcart,
    nature: r.nature as NatureEcart,
    repere: r.repere,
    auteur: texte(r.auteur),
    auteurUPN: texte(r.auteurUPN),
    date: texte(r.date),
    probleme: texte(r.probleme),
    fichiers: Array.isArray(r.fichiers) ? r.fichiers.filter((f): f is string => typeof f === 'string') : [],
  }
}

/** Toutes les lignes non vides de la colonne, dans l'ordre, lisibles ou non. */
export function lireEcarts(colonne: string | null | undefined): LigneEcart[] {
  return (colonne ?? '')
    .split(/\r?\n/)
    .filter((l) => l.trim() !== '')
    .map((brute) => {
      const ecart = lireLigneEcart(brute)
      return ecart ? { ecart, brute } : { ecart: null, brute }
    })
}

/** Les seuls écarts lisibles, dans l'ordre du relevé. */
export function ecartsLisibles(colonne: string | null | undefined): EcartReleve[] {
  return lireEcarts(colonne).flatMap((l) => (l.ecart ? [l.ecart] : []))
}

/** Lignes que l'appli ne sait pas lire (gardées dans la colonne, signalées sur la fiche). */
export function lignesIllisibles(colonne: string | null | undefined): string[] {
  return lireEcarts(colonne).flatMap((l) => (l.ecart ? [] : [l.brute]))
}

/** Un écart → sa ligne JSON (une seule ligne : le texte saisi ne peut pas la couper). */
export function ligneEcart(e: EcartReleve): string {
  return JSON.stringify({
    id: e.id,
    support: e.support,
    nature: e.nature,
    repere: e.repere.trim(),
    auteur: e.auteur,
    auteurUPN: e.auteurUPN,
    date: e.date,
    probleme: e.probleme,
    fichiers: e.fichiers,
  })
}

/** La colonne avec un écart de plus, en fin de texte ; les lignes déjà là restent à l'identique. */
export function ajouterLigneEcart(colonne: string | null | undefined, e: EcartReleve): string {
  const avant = (colonne ?? '').replace(/\s+$/, '')
  return avant ? `${avant}\n${ligneEcart(e)}` : ligneEcart(e)
}

/** L'écart est-il dans la colonne ? (contrôle après écriture : un autre a pu réécrire entre-temps) */
export function contientEcart(colonne: string | null | undefined, id: string): boolean {
  return id !== '' && ecartsLisibles(colonne).some((e) => e.id === id)
}

// ─── Corriger un écart depuis la fiche du relevé (22/10) ───

/** Ce qu'une correction change : le document, la nature, le repère. */
export type ChangementEcart = Pick<EcartReleve, 'support' | 'nature' | 'repere'>

/**
 * La colonne avec l'écart `id` corrigé SUR PLACE : document, nature et repère
 * changent ; id, auteur, date, problème lié et photos restent. Les autres lignes,
 * illisibles comprises, ne bougent pas, ni de contenu ni de place.
 */
export function remplacerLigneEcart(colonne: string | null | undefined, id: string, m: ChangementEcart): string {
  return lireEcarts(colonne)
    .map((l) => (l.ecart && l.ecart.id === id ? ligneEcart({ ...l.ecart, support: m.support, nature: m.nature, repere: m.repere.trim() }) : l.brute))
    .join('\n')
}

/** La colonne sans l'écart `id` ; ses photos restent dans leur dossier (rien n'est supprimé). */
export function retirerLigneEcart(colonne: string | null | undefined, id: string): string {
  return lireEcarts(colonne)
    .filter((l) => !(l.ecart && l.ecart.id === id))
    .map((l) => l.brute)
    .join('\n')
}

/** L'écart `id` a-t-il exactement ce contenu ? (contrôle après écriture) */
export function ecartVaut(colonne: string | null | undefined, id: string, m: ChangementEcart): boolean {
  const e = ecartsLisibles(colonne).find((x) => x.id === id)
  return !!e && e.support === m.support && e.nature === m.nature && e.repere.trim() === m.repere.trim()
}

/**
 * Qui corrige ou retire un écart d'un relevé : qui gère les actions de la FNC-T,
 * ou qui l'a relevé lui-même. Un écart sans identifiant (ligne ancienne) ne se
 * corrige pas : on ne saurait pas vérifier l'écriture.
 */
export function peutCorrigerEcart(e: Pick<EcartReleve, 'id' | 'auteurUPN'>, upn: string, gestionnaire: boolean): boolean {
  if (!e.id) return false
  return gestionnaire || (upn.trim() !== '' && e.auteurUPN.trim().toLowerCase() === upn.trim().toLowerCase())
}

/** Le dernier écart d'un relevé ne se retire pas : le relevé n'aurait plus d'objet (on le corrige plutôt). */
export function estDernierEcart(colonne: string | null | undefined): boolean {
  return ecartsLisibles(colonne).length <= 1
}

/** Historique « Écart modifié » : la phrase d'avant et celle d'après. */
export function texteEcartModifie(avant: EcartReleve, m: ChangementEcart): string {
  return `Avant : ${phraseDe(avant)}\nAprès : ${phraseDe(m)}`
}

/** Historique « Écart retiré » : la phrase, et d'où elle venait. */
export function texteEcartRetire(e: EcartReleve): string {
  const lignes = [phraseDe(e)]
  if (e.auteur) lignes.push(`Relevé par ${e.auteur}`)
  if (e.probleme) lignes.push(`Problème ${e.probleme}`)
  return lignes.join('\n')
}

/** Identifiant d'un nouvel écart : l'instant + quelques caractères au hasard. */
export function nouvelIdEcart(maintenant: Date = new Date(), hasard: () => number = Math.random): string {
  return `${maintenant.getTime().toString(36)}-${Math.floor(hasard() * 36 ** 4).toString(36).padStart(4, '0')}`
}

/** La phrase anglaise, celle que lira le client. */
export function phraseDe(e: Pick<EcartReleve, 'support' | 'nature' | 'repere'>): string {
  return phraseEcart({ support: e.support, nature: e.nature, texte: e.repere })
}

export interface GroupeEcarts {
  support: SupportEcart
  /** « P&ID », « PPL process », « PPL doc ». */
  libelle: string
  /** N° du test qui valide ce document (75250, 76326, 75255). */
  test: string
  ecarts: EcartReleve[]
}

/** Les écarts par document, dans l'ordre P&ID, PPL process, PPL doc ; un document sans écart n'apparaît pas. */
export function ecartsParSupport(ecarts: readonly EcartReleve[]): GroupeEcarts[] {
  return SUPPORTS_ECART.map((s) => ({
    support: s.valeur,
    libelle: s.libelle,
    test: s.test,
    ecarts: ecarts.filter((e) => e.support === s.valeur),
  })).filter((g) => g.ecarts.length > 0)
}

/** Les problèmes cités par les écarts, sans doublon, dans l'ordre du relevé. */
export function problemesDesEcarts(ecarts: readonly EcartReleve[]): string[] {
  const vus = new Set<string>()
  const res: string[] = []
  for (const e of ecarts) {
    const id = e.probleme.trim()
    if (id && !vus.has(id.toUpperCase())) {
      vus.add(id.toUpperCase())
      res.push(id)
    }
  }
  return res
}

// ─── Relever un écart depuis un problème (étape 3a-2) ───

/** NVS d'un équipement, lu dans sa fourniture Windchill ; vide s'il ne se lit pas. */
export function nvsDe(eq: Pick<Equipement, 'numeroSerie' | 'fourniture'> | undefined): string {
  if (!eq) return ''
  return nvsDeFourniture(eq.fourniture, CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(eq.numeroSerie)] ?? null)
}

export interface MachineReleve {
  numeroSerie: string
  nvs: string
  /** Même NVS que la machine du problème : même P&ID, même PPL. */
  cochable: boolean
  /** Pourquoi elle ne l'est pas. */
  raison?: 'nvs-different' | 'sans-nvs'
  duProbleme: boolean
}

/**
 * Les machines montrées dans « Relever un écart » : celle du problème, ses sœurs
 * (même défaut, Lot 14), les autres machines du projet et toute machine de même
 * NVS. Seules celles du MÊME NVS que la machine du problème sont cochables
 * (décision du 14/10) ; une machine sans NVS lisible ne l'est jamais — et si
 * c'est celle du problème, aucune ne l'est : pas de relevé possible.
 * Ordre : le problème, les cochables, puis les autres, par N° de série.
 */
export function machinesDuReleve(
  probleme: { numeroSerie: string; pn: string },
  soeurs: readonly string[],
  referentiel: readonly Equipement[],
): { nvs: string; machines: MachineReleve[] } {
  const parNs = new Map(referentiel.map((eq) => [cleRapprochement(eq.numeroSerie), eq]))
  const cleProbleme = cleRapprochement(probleme.numeroSerie)
  const nvs = nvsDe(parNs.get(cleProbleme))
  const pn = cleRapprochement(probleme.pn)
  const candidats = [
    probleme.numeroSerie,
    ...soeurs,
    ...referentiel
      .filter((eq) => (pn && cleRapprochement(eq.pn) === pn) || (nvs && nvsDe(eq) === nvs))
      .map((eq) => eq.numeroSerie),
  ]
  const vues = new Set<string>()
  const machines: MachineReleve[] = []
  for (const brut of candidats) {
    const cle = cleRapprochement(brut)
    if (!cle || vues.has(cle)) continue
    vues.add(cle)
    const eq = parNs.get(cle)
    const n = nvsDe(eq)
    const cochable = nvs !== '' && n === nvs
    machines.push({
      numeroSerie: (eq?.numeroSerie ?? brut).trim().toUpperCase(),
      nvs: n,
      cochable,
      ...(cochable ? {} : { raison: n ? ('nvs-different' as const) : ('sans-nvs' as const) }),
      duProbleme: cle === cleProbleme,
    })
  }
  const rang = (m: MachineReleve): number => (m.duProbleme ? 0 : m.cochable ? 1 : 2)
  machines.sort((a, b) => rang(a) - rang(b) || a.numeroSerie.localeCompare(b.numeroSerie, 'fr'))
  return { nvs, machines }
}

/**
 * Les relevés encore ouverts (pas ouverts en FNC-T, pas clos) qui portent l'une
 * de ces machines — retrouvés par le N° de série (colonne NumerosSerie, pas de
 * colonne NVS). Du plus ancien au plus récent.
 */
export function relevesOuvertsPour(fncts: readonly Fnct[], machines: readonly string[]): Fnct[] {
  const cles = new Set(machines.map(cleRapprochement).filter(Boolean))
  return fncts
    .filter((f) => estEnReleve(f) && f.numerosSerie.some((ns) => cles.has(cleRapprochement(ns))))
    .sort((a, b) => comparerNumeros(a.numero, b.numero))
}

/** Les machines du relevé après l'ajout : les siennes, puis les nouvelles cochées (sans doublon). */
export function machinesApresAjout(existantes: readonly string[], cochees: readonly string[]): string[] {
  const res = [...existantes]
  const vues = new Set(existantes.map(cleRapprochement))
  for (const ns of cochees) {
    const cle = cleRapprochement(ns)
    if (cle && !vues.has(cle)) {
      vues.add(cle)
      res.push(ns.trim().toUpperCase())
    }
  }
  return res
}

/** Ce qui manque pour relever l'écart, en clair ; vide = prêt. */
export function manquantsEcart(s: { repere: string; machines: readonly string[] }): string[] {
  const m: string[] = []
  if (s.machines.length === 0) m.push('un équipement')
  if (!s.repere.trim()) m.push('le repère')
  return m
}

/**
 * La FNC-T créée au premier écart : en relevé (ouverture vide), N° pris tout de
 * suite, enregistrée et détectée le jour de l'écart. Seul ce qui est sûr est écrit
 * (machines, modèle, type, CVE, catégorie Process, détection en test) ; leader,
 * expert, priorité, description et documents impactés se décident à l'ouverture.
 */
export function fnctEnReleve(p: {
  annee: number
  rang: number
  aujourdhui: Jour
  machines: readonly string[]
  equipement: Pick<Equipement, 'familleEquipement' | 'designation' | 'cve'> | null | undefined
  enregistrePar: string
  ecart: EcartReleve
}): Fnct {
  return {
    numero: formatNumero(p.annee, p.rang),
    annee: p.annee,
    rang: p.rang,
    enregistrement: p.aujourdhui,
    enregistrePar: p.enregistrePar,
    leader: '',
    expert: '',
    modele: p.equipement?.familleEquipement ?? '',
    typeProduit: typeProduitDe(p.equipement?.designation),
    numerosSerie: machinesApresAjout([], p.machines),
    cve: (p.equipement?.cve ?? '').trim(),
    categorie: CATEGORIE_PROCESS,
    documentsImpactes: '',
    description: '',
    lieuDetection: 'I-Test',
    dateDetection: p.aujourdhui,
    priorite: '',
    explicationPriorite: '',
    severite: null,
    frequence: null,
    detectabilite: null,
    analyseRisque: '',
    clotureCorrection: null,
    pretInvestigation: null,
    clotureInvestigation: null,
    numeroCapa: '',
    commentaires: '',
    legitimite: '',
    cloture: null,
    ecarts: ligneEcart(p.ecart),
    ouverture: null,
  }
}

/** Texte de l'historique pour un écart relevé : la phrase, et les machines ajoutées s'il y en a. */
export function texteEcartReleve(e: EcartReleve, machinesAjoutees: readonly string[]): string {
  const lignes = [phraseDe(e)]
  if (e.probleme) lignes.push(`Problème ${e.probleme}`)
  if (machinesAjoutees.length > 0) lignes.push(`Équipement${machinesAjoutees.length > 1 ? 's' : ''} ajouté${machinesAjoutees.length > 1 ? 's' : ''} : ${machinesAjoutees.join(', ')}`)
  return lignes.join('\n')
}

/**
 * Les problèmes proposés à « Lier un problème » quand on relève depuis la fiche
 * du relevé (étape 3b) : ceux des machines du relevé, jamais un annulé ; les
 * actifs d'abord, puis du plus récent au plus ancien ; `max` au plus.
 */
export function problemesDuReleve<T extends Pick<Issue, 'id' | 'numeroSerie' | 'statut' | 'dateDeclaration'>>(
  issues: readonly T[],
  machines: readonly string[],
  max = 8,
): T[] {
  const cles = new Set(machines.map(cleRapprochement).filter(Boolean))
  return issues
    .filter((i) => i.statut !== ANNULE && cles.has(cleRapprochement(i.numeroSerie)))
    .sort((a, b) => Number(estActif(b)) - Number(estActif(a)) || b.dateDeclaration.localeCompare(a.dateDeclaration))
    .slice(0, max)
}

// ─── Photos d'un écart (étape 3b-2) ───
//
// Une seule bibliothèque, SCE_PiecesJointes (décision 5 du 14/10) : le dossier du
// problème lié, sinon celui du N° de FNC-T (`FNC-T_2026-60/`). L'écart ne garde
// que les NOMS (jamais d'adresse : les listes doivent pouvoir déménager).

/** Le dossier de SCE_PiecesJointes où vont (et se relisent) les photos d'un écart. */
export function dossierPhotosEcart(e: Pick<EcartReleve, 'probleme'>, numero: string): string {
  return e.probleme.trim() || numero
}

/** Les dossiers à lire pour montrer les photos des écarts, sans doublon. */
export function dossiersDesEcarts(ecarts: readonly EcartReleve[], numero: string): string[] {
  return [...new Set(ecarts.filter((e) => e.fichiers.length > 0).map((e) => dossierPhotosEcart(e, numero)))]
}

/**
 * Les fichiers d'un écart, retrouvés par leur nom dans son dossier, dans l'ordre
 * de l'écart. Un nom absent (fichier supprimé, renommé) est simplement omis.
 */
export function fichiersDeLEcart<F extends { nom: string }>(
  e: Pick<EcartReleve, 'probleme' | 'fichiers'>,
  numero: string,
  parDossier: ReadonlyMap<string, readonly F[]>,
): F[] {
  const dossier = parDossier.get(dossierPhotosEcart(e, numero)) ?? []
  return e.fichiers.flatMap((nom) => dossier.filter((f) => f.nom === nom))
}
